/**
 * Step-by-step setup guides shown on the Connections page drawer.
 * OAuth providers also use oauthProvidersMeta.js for BYOC checklist details.
 */
import { getOAuthProviderMeta } from './oauthProvidersMeta';

const AFTER_CONNECT =
  'Open **AI Studio → Chat**, select your agent, and click **Link** on the integration panel (or toggle the connection under **Agents**).';

const CATEGORY_USED_FOR = {
  Email: 'Marketing emails, outreach, and AI Studio email actions',
  'SMS & Voice': 'SMS campaigns, voice alerts, and automations',
  Messaging: 'Slack/Discord/WhatsApp notifications and team alerts',
  Scheduling: 'Booking links and calendar-aware automations',
  Payments: 'Payment events and checkout automations',
  AI: 'AI agent workflow steps (optional — platform Settings may already have a key)',
  CRM: 'Contacts, deals, and CRM actions in AI Studio agents',
  Support: 'Tickets and customer support automations',
  Productivity: 'Docs, tasks, and workspace integrations',
  'E-commerce': 'Store and order automations',
  Storage: 'Files and asset access for agents',
  'Dev Tools': 'Repos, issues, and developer workflows',
  Integrations: 'Cross-app automations and AI Studio live actions',
};

const CATEGORY_STEPS = {
  Email: [
    'Create an account or sign in to your email provider.',
    'Generate an API key (or SMTP credentials) with send permissions.',
    'Paste the credentials in the fields below and click **Connect**.',
  ],
  'SMS & Voice': [
    'Open your SMS/voice provider dashboard and create an API key (or SID + token).',
    'Fill the fields below exactly as labeled (phone numbers in E.164 when asked).',
    'Click **Connect**, then use Marketing Suite SMS / Voice Campaigns / automations.',
  ],
  Messaging: [
    'Open the provider dashboard and create a webhook, bot token, or API key.',
    'Paste the values into the fields below (only required fields marked *).',
    'Click **Connect**, then use WhatsApp Marketing, Slack alerts, or AI Studio Link.',
  ],
  CRM: [
    'If using OAuth: create a developer app and add our redirect URI (shown in the OAuth panel).',
    'If using API key: copy credentials from your CRM admin / developer settings.',
    'Connect here, then link the integration to your agent in AI Studio.',
  ],
};

const PROVIDER_GUIDES = {
  sendgrid: {
    steps: [
      'Sign in at sendgrid.com → Settings → API Keys.',
      'Create an API key with **Mail Send** (Full Access or Restricted with send).',
      'Paste the key below — it is only shown once in SendGrid.',
      'Verify a sender identity (Single Sender or Domain Authentication) in SendGrid before sending.',
    ],
    links: [
      { label: 'SendGrid API keys', url: 'https://app.sendgrid.com/settings/api_keys' },
      { label: 'Sender verification', url: 'https://docs.sendgrid.com/ui/account-and-settings/how-to-set-up-domain-authentication' },
    ],
    usedFor: 'Transactional email from Marketing and AI Studio agents',
  },
  mailgun: {
    steps: [
      'Log in to Mailgun → Sending → Domains and note your **domain** (e.g. mg.yoursite.com).',
      'Go to Settings → API Keys and create a **Private API key**.',
      'Enter domain + API key below, then click **Connect**.',
    ],
    links: [{ label: 'Mailgun dashboard', url: 'https://app.mailgun.com/' }],
    usedFor: 'Outbound email via your Mailgun domain',
  },
  ses: {
    steps: [
      'In AWS Console → IAM, create a user with **AmazonSESFullAccess** (or least-privilege send policy).',
      'Create access key ID + secret access key.',
      'Verify your sending domain or email in Amazon SES.',
      'Enter credentials and region (e.g. us-east-1) below.',
    ],
    links: [{ label: 'Amazon SES console', url: 'https://console.aws.amazon.com/ses/' }],
    usedFor: 'High-volume email through AWS SES',
  },
  smtp: {
    steps: [
      'Get SMTP host, port, username, and password from your provider (Gmail App Password, Outlook, etc.).',
      'Common: Gmail → Google Account → Security → 2-Step ON → App passwords.',
      'Host: smtp.gmail.com, Port: 587, TLS: true.',
      'Fill all fields below and click **Connect**.',
    ],
    links: [
      { label: 'Gmail app passwords', url: 'https://myaccount.google.com/apppasswords' },
    ],
    usedFor: 'Any SMTP server (Gmail, Outlook, custom host)',
  },
  resend: {
    steps: [
      'Sign up at resend.com → API Keys → Create API Key.',
      'Verify your domain under Domains (required for production sending).',
      'Paste the API key below.',
    ],
    links: [{ label: 'Resend dashboard', url: 'https://resend.com/api-keys' }],
    usedFor: 'Developer-friendly transactional email',
  },
  twilio: {
    steps: [
      'Sign in at twilio.com → Account → API keys & tokens.',
      'Copy **Account SID** and **Auth Token**.',
      'Buy or verify a phone number for SMS (Phone Numbers → Manage).',
      'Optional: set **From number** in E.164 format (+14155551234).',
    ],
    links: [{ label: 'Twilio console', url: 'https://console.twilio.com/' }],
    usedFor: 'SMS, MMS, and voice automations',
  },
  textlocal: {
    steps: [
      'Log in to textlocal.in (or .com) → Settings → API Keys.',
      'Copy your API key and registered **Sender ID**.',
      'Enter both below.',
    ],
    links: [{ label: 'Textlocal', url: 'https://www.textlocal.in/' }],
    usedFor: 'Bulk SMS (UK/India)',
  },
  slack: {
    steps: [
      'Option A — **Incoming webhook**: Slack app → Incoming Webhooks → Add to workspace → copy URL.',
      'Option B — **Bot token**: Create a Slack app → OAuth & Permissions → install to workspace → copy **Bot User OAuth Token** (xoxb-…).',
      'Paste webhook URL and/or bot token below (at least one required).',
    ],
    links: [
      { label: 'Slack API apps', url: 'https://api.slack.com/apps' },
    ],
    usedFor: 'Channel notifications from automations and agents',
  },
  'slack-oauth': {
    steps: [
      'Create a Slack app at api.slack.com → OAuth & Permissions.',
      'Add the redirect URI from the OAuth panel below.',
      'Add bot scopes (e.g. chat:write, channels:read) and install to workspace.',
      'Click **Connect Slack** — approve in the popup.',
    ],
    links: [{ label: 'Slack app settings', url: 'https://api.slack.com/apps' }],
    usedFor: 'OAuth-based Slack messaging',
  },
  evolution: {
    steps: [
      'Run Evolution API locally (Docker script `scripts/start-evolution-api.ps1`) or use your hosted Evolution URL.',
      'Copy **AUTHENTICATION_API_KEY** from Evolution’s `.env` — that is the **API key** below (must match backend `EVOLUTION_API_KEY` if you also set Settings).',
      'Enter **Base URL** without a trailing slash (e.g. `http://localhost:8080` or your public Evolution URL).',
      'Paste the API key, name the connection, click **Connect**.',
      'Open **Marketing Suite → WhatsApp**, create/connect an instance, and **scan the QR** on your phone.',
    ],
    links: [
      { label: 'Evolution API docs', url: 'https://doc.evolution-api.com/' },
    ],
    usedFor: 'WhatsApp Business messaging via Evolution API (QR / send / broadcasts)',
    afterConnect:
      'Go to **Marketing Suite → WhatsApp** to create an instance and scan the QR. You can also link this connection to an AI Studio agent.',
  },
  httpsms: {
    steps: [
      'Install the httpSMS Android app and create an account at httpsms.com.',
      'Copy your **API key** from the httpSMS dashboard.',
      'Enter the phone number of the SIM that will send SMS (**From number**, E.164 like +14155551234).',
      'Paste both below and click **Connect**.',
    ],
    links: [{ label: 'httpSMS', url: 'https://httpsms.com/' }],
    usedFor: 'Send SMS through your Android phone (no Twilio required)',
  },
  elevenlabs: {
    steps: [
      'Sign in at elevenlabs.io → Profile → API keys.',
      'Create a key and paste it below.',
      'Use with Voice Campaigns / AI voice automations (BYOK).',
    ],
    links: [{ label: 'ElevenLabs API keys', url: 'https://elevenlabs.io/app/settings/api-keys' }],
    usedFor: 'AI voice synthesis for outbound calls and voice bots',
  },
  discord: {
    steps: [
      'Open Discord channel → Edit Channel → Integrations → Webhooks → New Webhook.',
      'Copy the **Webhook URL**.',
      'Paste it below and click **Connect**.',
    ],
    links: [{ label: 'Discord webhooks guide', url: 'https://support.discord.com/hc/en-us/articles/228383668' }],
    usedFor: 'Discord channel notifications',
  },
  calcom: {
    steps: [
      'Sign in to cal.com → Settings → Developer → API Keys (optional).',
      'Note your public **username** (cal.com/yourname).',
      'Enter username below; add API key if you use Cal.com API automations.',
    ],
    links: [{ label: 'Cal.com settings', url: 'https://cal.com/settings/developer' }],
    usedFor: 'Booking links in sales and lead flows',
  },
  stripe: {
    steps: [
      'Stripe Dashboard → Developers → API keys.',
      'Copy the **Secret key** (sk_live_… or sk_test_… for testing).',
      'Paste below — never expose this key in client-side code.',
    ],
    links: [{ label: 'Stripe API keys', url: 'https://dashboard.stripe.com/apikeys' }],
    usedFor: 'Payments and checkout automations',
  },
  openai: {
    steps: [
      'platform.openai.com → API keys → Create new secret key.',
      'Paste below. Platform **Settings** may already store an OpenAI key for AI Studio.',
      'Use this connection only if automations need a separate key.',
    ],
    links: [{ label: 'OpenAI API keys', url: 'https://platform.openai.com/api-keys' }],
    usedFor: 'Optional per-automation OpenAI access',
  },
  hubspot: {
    steps: [
      'developers.hubspot.com → Create app → Auth tab.',
      'Add redirect URI from the OAuth panel (must match exactly).',
      'Include scopes: crm.objects.contacts.read, crm.objects.contacts.write (and deals if needed).',
      'If platform OAuth is enabled: click **Connect HubSpot** directly. Otherwise paste Client ID + Secret.',
      'After connecting, link HubSpot to your agent in AI Studio.',
    ],
    links: [
      { label: 'HubSpot developers', url: 'https://developers.hubspot.com/' },
      { label: 'OAuth guide', url: 'https://developers.hubspot.com/docs/api/oauth-quickstart-guide' },
    ],
    usedFor: 'List/create CRM contacts in AI Studio agents',
  },
  gmail: {
    steps: [
      'Google Cloud Console → APIs & Services → Enable **Gmail API**.',
      'Create OAuth 2.0 Client ID (Web application).',
      'Add redirect URI from the OAuth panel below.',
      'Click **Connect Gmail** and sign in with the Google account to use.',
    ],
    links: [
      { label: 'Google Cloud Console', url: 'https://console.cloud.google.com/apis/credentials' },
    ],
    usedFor: 'Gmail profile and email-related integrations',
  },
  outlook: {
    steps: [
      'Azure Portal → App registrations → New registration.',
      'Add redirect URI (Web) from the OAuth panel below.',
      'API permissions: Microsoft Graph (Mail.Read, User.Read, etc.).',
      'Create a client secret and use Application (client) ID + secret if BYOC.',
      'Click **Connect Outlook** to authorize.',
    ],
    links: [
      { label: 'Azure app registrations', url: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
    ],
    usedFor: 'Microsoft email and profile',
  },
  'google-calendar': {
    steps: [
      'Google Cloud Console → Enable **Google Calendar API**.',
      'OAuth client with redirect URI from the panel below.',
      'Connect and grant calendar access.',
    ],
    links: [{ label: 'Google Calendar API', url: 'https://console.cloud.google.com/apis/library/calendar-json.googleapis.com' }],
    usedFor: 'List calendars and scheduling automations',
  },
  'google-drive': {
    steps: [
      'Google Cloud Console → Enable **Google Drive API**.',
      'OAuth client with redirect URI from the panel below.',
      'Connect and grant Drive file access.',
    ],
    links: [{ label: 'Google Drive API', url: 'https://console.cloud.google.com/apis/library/drive.googleapis.com' }],
    usedFor: 'List and access files for agents and automations',
  },
  salesforce: {
    steps: [
      'Salesforce Setup → App Manager → New Connected App.',
      'Enable OAuth, add callback URL from the OAuth panel.',
      'Select scopes (api, refresh_token, offline_access).',
      'Use Consumer Key + Secret if BYOC, or platform Connect if available.',
    ],
    links: [{ label: 'Salesforce connected apps', url: 'https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm' }],
    usedFor: 'Salesforce CRM automations',
  },
  notion: {
    steps: [
      'notion.so/my-integrations → New integration.',
      'Copy OAuth client ID & secret (Public integration) or use internal integration token flow per Notion docs.',
      'Add redirect URI from the panel for OAuth.',
      'Share pages/databases with the integration after connecting.',
    ],
    links: [{ label: 'Notion integrations', url: 'https://www.notion.so/my-integrations' }],
    usedFor: 'Notion workspace access',
  },
  github: {
    steps: [
      'GitHub → Settings → Developer settings → OAuth Apps → New OAuth App.',
      'Authorization callback URL = redirect URI from the panel below.',
      'Register and paste Client ID + Secret (BYOC) or use **Connect GitHub**.',
    ],
    links: [{ label: 'GitHub OAuth apps', url: 'https://github.com/settings/developers' }],
    usedFor: 'Repos and GitHub automations',
  },
  shopify: {
    steps: [
      'Shopify Partners → Apps → Create app → Configure Admin API / OAuth.',
      'Set redirect URL from the OAuth panel.',
      'Enter your **shop domain** (my-store) when connecting.',
    ],
    links: [{ label: 'Shopify partners', url: 'https://partners.shopify.com/' }],
    usedFor: 'Shop and order data',
  },
  zendesk: {
    steps: [
      'Zendesk Admin → Apps and integrations → APIs → OAuth clients.',
      'Add redirect URI from the panel.',
      'Connect with appropriate read/write ticket scopes.',
    ],
    links: [{ label: 'Zendesk OAuth', url: 'https://developer.zendesk.com/api-reference/ticketing/oauth/oauth/' }],
    usedFor: 'Support tickets and helpdesk',
  },
  pipedrive: {
    steps: [
      'Pipedrive Marketplace → Developer hub → Create OAuth app.',
      'Set callback URL from the OAuth panel.',
      'Connect and authorize your Pipedrive account.',
    ],
    links: [{ label: 'Pipedrive developers', url: 'https://developers.pipedrive.com/' }],
    usedFor: 'Pipedrive CRM sales automations',
  },
  airtable: {
    steps: [
      'airtable.com/create/oauth → Register OAuth integration.',
      'Add redirect URI from the panel.',
      'Connect and grant access to bases you need.',
    ],
    links: [{ label: 'Airtable OAuth', url: 'https://airtable.com/developers/web/api/oauth-reference' }],
    usedFor: 'Airtable bases and records',
  },
};

const DEFAULT_GUIDE = {
  steps: [
    'Click this integration tile to open setup.',
    'Follow the fields or OAuth flow in the side panel.',
    'After **Connected** appears, link it to an AI Studio agent or automation.',
  ],
  usedFor: 'Automations and AI Studio agents',
};

/**
 * @param {{ id?: string, label?: string, auth_type?: string, category?: string }} provider
 */
export function getConnectionSetupGuide(provider) {
  const id = String(provider?.id || '').trim();
  const specific = PROVIDER_GUIDES[id];
  if (specific) {
    return {
      title: `How to connect ${provider.label || id}`,
      steps: specific.steps,
      links: specific.links || [],
      usedFor: specific.usedFor || CATEGORY_USED_FOR[provider.category] || DEFAULT_GUIDE.usedFor,
      afterConnect: specific.afterConnect || AFTER_CONNECT,
    };
  }

  if (provider?.auth_type === 'oauth2') {
    const oauth = getOAuthProviderMeta(id);
    return {
      title: `How to connect ${provider.label || oauth.label || id}`,
      steps: oauth.setupSteps?.length
        ? oauth.setupSteps
        : (CATEGORY_STEPS.CRM || DEFAULT_GUIDE.steps),
      links: [
        oauth.developerPortalUrl && { label: 'Developer portal', url: oauth.developerPortalUrl },
        oauth.docsUrl && { label: 'Documentation', url: oauth.docsUrl },
      ].filter(Boolean),
      usedFor: CATEGORY_USED_FOR[provider.category] || DEFAULT_GUIDE.usedFor,
      afterConnect: AFTER_CONNECT,
    };
  }

  const catSteps = CATEGORY_STEPS[provider?.category];
  return {
    title: `How to connect ${provider?.label || id}`,
    steps: catSteps || DEFAULT_GUIDE.steps,
    links: [],
    usedFor: CATEGORY_USED_FOR[provider?.category] || DEFAULT_GUIDE.usedFor,
    afterConnect: AFTER_CONNECT,
  };
}

export const CONNECTIONS_PAGE_INTRO = {
  title: 'How Connections work',
  steps: [
    '**Connect** an app here once (API key or OAuth).',
    'Go to **AI Studio** and create or open an agent.',
    'Use the **integration panel** to **Link** connected apps to that agent.',
    'Chat with your agent — it can call live tools (e.g. HubSpot list contacts).',
  ],
};
